/**
 * 運送PL(Board・2026-10-23 発注者指示「まず運送の PL を作成」)。形は SC Board_v2「運送」シート。
 *
 *  - 行の並び: 売上合計(元請け > 拠点 > コース > ドライバー)→ 原価合計(人件費: 元請け > 拠点 > ドライバー、ガソリン代: 元請け > 拠点 > ドライバー)
 *    → 粗利 → 販管 → 営業利益 → 営業外収益・費用 → 経常利益 → 特別利益・特別損失 → 税引前当期純利益
 *  - 数字の出どころは2つ
 *     1. Routine の行(routine_pl): 売上・人件費・ガソリン代・特別利益(保険・修理積立・ペナルティ・インボイス)。
 *        速報(live)と確定(confirmed)の印を月ごとに持つ(速報 = 締め前の参考、確定 = 正)
 *     2. Gate の取引(運送の事業部): 科目① > 科目②。実績だけを月の列に入れる(判定は plEntryOf()。状態を直接並べない)。
 *        Routine から取り込んだ取引(source / external が routine)は Routine の行と二重になるため入れない
 *  - 利益は v8 と同じ: 粗利 = 売上 − 原価 / 営業利益 = 粗利 − 販管 / 経常利益 = 営業利益 + 外収 − 外費 / 税引前 = 経常利益 + 特利 − 特損
 *  - 全社PL の5行(共通サマリー)はこの表では変えない(別の表示)
 *
 * 具体例は tests/unit/unsoPl.test.ts
 */
import { sectionOf, type SectionKey } from '@core/pl/accountPl';
import { plEntryOf } from '@core/pl/plRecognition';
import type { Account, DateStr, MonthStr, RoutinePlLine, SubAccount, Transaction, Yen } from '@core/lib/types';

export const UNSO_DEPARTMENT_ID = 'unso' as const;

export type StageMark = 'live' | 'confirmed' | 'mixed' | null;

export type UnsoNode = {
  key: string;
  label: string;
  /** 0 = 区分の見出し・利益の行 */
  depth: number;
  kind: 'section' | 'group' | 'leaf' | 'profit';
  /** 列の月ごとの金額 */
  amounts: Record<MonthStr, Yen>;
  /** 列の月ごとの速報・確定(Routine の行だけ。Gate の行は null) */
  stages: Record<MonthStr, StageMark>;
  /** 選んだ月の単価・数量(葉だけ。無ければ一番新しい月の値) */
  unitPrice: Yen | null;
  quantity: number | null;
  quantityUnit: string | null;
  method: string | null;
  gasClass: string | null;
  source: 'Routine' | 'Gate' | null;
  children: UnsoNode[];
};

export type UnsoPl = {
  /** 列の月(古い順)。最後が選んだ月 */
  months: MonthStr[];
  month: MonthStr;
  rows: UnsoNode[];
  /** 列の月にある Routine の行の数(0 なら Routine 未接続) */
  routineLineCount: number;
  /** 選んだ月の Gate の見込み(申請中・承認待ちなど。表の金額には入れない) */
  gatePending: { count: number; amount: Yen };
  /** マスタに無い科目の Gate の取引の件数 */
  unknownAccountCount: number;
};

const SECTION_DEFS: Array<{ key: SectionKey; label: string }> = [
  { key: 'sales', label: '売上合計' },
  { key: 'cogs', label: '原価合計' },
  { key: 'sga', label: '販売費及び一般管理費' },
  { key: 'non_op_income', label: '営業外収益' },
  { key: 'non_op_expense', label: '営業外費用' },
  { key: 'extra_income', label: '特別利益' },
  { key: 'extra_loss', label: '特別損失' },
];

const SELF_AFFILIATION = '自社社員';

/** Routine から取り込んだ取引か(運送PL では Routine の行で出すため、Gate の行には入れない) */
export function isRoutineTransaction(t: Pick<Transaction, 'source'> & { external?: { source_system?: string } | null }): boolean {
  return (t.source as string) === 'routine' || t.external?.source_system === 'routine';
}

function emptyMonths<T>(months: MonthStr[], v: T): Record<MonthStr, T> {
  return Object.fromEntries(months.map((m) => [m, v])) as Record<MonthStr, T>;
}

function newNode(key: string, label: string, depth: number, kind: UnsoNode['kind'], months: MonthStr[], source: UnsoNode['source']): UnsoNode {
  return {
    key,
    label,
    depth,
    kind,
    amounts: emptyMonths(months, 0),
    stages: emptyMonths<StageMark>(months, null),
    unitPrice: null,
    quantity: null,
    quantityUnit: null,
    method: null,
    gasClass: null,
    source,
    children: [],
  };
}

function mergeStage(a: StageMark, b: StageMark): StageMark {
  if (a === null) return b;
  if (b === null) return a;
  return a === b ? a : 'mixed';
}

/** 子の金額・速報/確定を親へ積み上げる(葉が持つ自分の値はそのまま) */
function rollUp(node: UnsoNode, months: MonthStr[]): void {
  for (const c of node.children) {
    rollUp(c, months);
    for (const m of months) {
      node.amounts[m] = (node.amounts[m] ?? 0) + (c.amounts[m] ?? 0);
      node.stages[m] = mergeStage(node.stages[m] ?? null, c.stages[m] ?? null);
    }
  }
}

type Trie = {
  label: string;
  order: number;
  seq: number;
  children: Map<string, Trie>;
  lines: RoutinePlLine[];
};

/**
 * Routine の行を、path の階層の木にする。path の最後の段がその行を持つ。
 * 行を持つ段に子もあるとき(例: ドライバーの「売上」と「ガソリン売上」)は、自分の行を ownLabel の子として先頭に出す
 */
function routineTree(params: {
  lines: RoutinePlLine[];
  path: (l: RoutinePlLine) => string[];
  ownLabel: (l: RoutinePlLine) => string;
  keyPrefix: string;
  depth: number;
  months: MonthStr[];
  month: MonthStr;
}): UnsoNode[] {
  const { lines, path, ownLabel, keyPrefix, depth, months, month } = params;
  let seq = 0;
  const root: Trie = { label: '', order: Infinity, seq: 0, children: new Map(), lines: [] };
  for (const l of lines) {
    let t = root;
    for (const seg of path(l).filter((s) => s !== '')) {
      let c = t.children.get(seg);
      if (!c) {
        c = { label: seg, order: Infinity, seq: seq++, children: new Map(), lines: [] };
        t.children.set(seg, c);
      }
      c.order = Math.min(c.order, l.order ?? Infinity);
      t = c;
    }
    t.lines.push(l);
  }

  const leafOf = (key: string, label: string, d: number, ls: RoutinePlLine[]): UnsoNode => {
    const n = newNode(key, label, d, 'leaf', months, 'Routine');
    for (const l of ls) {
      if (!(l.month in n.amounts)) continue;
      n.amounts[l.month] = (n.amounts[l.month] ?? 0) + l.amount;
      n.stages[l.month] = mergeStage(n.stages[l.month] ?? null, l.stage);
    }
    // 単価・数量は選んだ月の行、無ければ一番新しい月の行から(同じ葉に行が2つあるときは単価・数量を出さない)
    const sorted = [...ls].sort((a, b) => (a.month < b.month ? 1 : -1));
    const pick = sorted.find((l) => l.month === month) ?? sorted[0];
    const sameMonth = ls.filter((l) => l.month === pick?.month);
    if (pick) {
      if (sameMonth.length === 1) {
        n.unitPrice = pick.unit_price;
        n.quantity = pick.quantity;
        n.quantityUnit = pick.quantity_unit;
      }
      n.method = pick.method;
      n.gasClass = pick.gas_class;
    }
    return n;
  };

  const build = (t: Trie, key: string, d: number): UnsoNode => {
    const kids = [...t.children.values()].sort((a, b) => a.order - b.order || a.seq - b.seq);
    if (kids.length === 0) return leafOf(key, t.label, d, t.lines);
    const n = newNode(key, t.label, d, 'group', months, 'Routine');
    if (t.lines.length > 0) n.children.push(leafOf(`${key}/(own)`, ownLabel(t.lines[0]!), d + 1, t.lines));
    for (const c of kids) n.children.push(build(c, `${key}/${c.label}`, d + 1));
    return n;
  };

  return [...root.children.values()]
    .sort((a, b) => a.order - b.order || a.seq - b.seq)
    .map((c) => build(c, `${keyPrefix}/${c.label}`, depth));
}

const driverLabel = (l: RoutinePlLine, withAffiliation: boolean): string => {
  const name = l.driver_name ?? l.driver_code ?? '';
  if (!name) return l.item;
  return withAffiliation && l.affiliation && l.affiliation !== SELF_AFFILIATION ? `${name}(${l.affiliation})` : name;
};

export function buildUnsoPl(params: {
  lines: RoutinePlLine[];
  transactions: Transaction[];
  accounts: Account[];
  subAccounts: SubAccount[];
  /** 古い順。最後が選んだ月 */
  months: MonthStr[];
  today?: DateStr;
}): UnsoPl {
  const { accounts, subAccounts, months, today } = params;
  const month = months[months.length - 1]!;
  const monthSet = new Set(months);
  const lines = params.lines.filter((l) => monthSet.has(l.month));
  const bySection = (s: RoutinePlLine['section']) => lines.filter((l) => l.section === s);
  const common = { months, month };

  // --- Routine の行 ---
  const salesNodes = routineTree({
    ...common,
    lines: bySection('sales'),
    path: (l) => [l.client, l.location, l.course ?? '', driverLabel(l, false), l.driver_name && l.item !== '売上' ? l.item : ''],
    ownLabel: (l) => l.item,
    keyPrefix: 'sales/routine',
    depth: 1,
  });
  const laborNodes = routineTree({
    ...common,
    lines: bySection('labor'),
    path: (l) => [l.client, l.location, driverLabel(l, true), l.driver_name && l.item !== '報酬' ? l.item : ''],
    ownLabel: (l) => l.item,
    keyPrefix: 'cogs/labor',
    depth: 2,
  });
  const fuelNodes = routineTree({
    ...common,
    lines: bySection('fuel'),
    path: (l) => [l.client, l.location, driverLabel(l, false)],
    ownLabel: (l) => l.item,
    keyPrefix: 'cogs/fuel',
    depth: 2,
  });
  const extraTree = (s: 'extra_income' | 'extra_loss') =>
    routineTree({
      ...common,
      lines: bySection(s),
      path: (l) => [l.item, l.driver_name ? driverLabel(l, true) : l.client],
      ownLabel: (l) => l.item,
      keyPrefix: `${s}/routine`,
      depth: 1,
    });

  // --- Gate の取引(科目① > 科目②) ---
  const accountById = new Map(accounts.map((a) => [a.id, a]));
  const subById = new Map(subAccounts.map((s) => [s.id, s]));
  const accAmounts = new Map<string, Record<MonthStr, Yen>>();
  const subAmounts = new Map<string, Map<string | null, Record<MonthStr, Yen>>>();
  let unknownAccountCount = 0;
  const gatePending = { count: 0, amount: 0 };
  for (const t of params.transactions) {
    if (t.type === 'transfer' || isRoutineTransaction(t)) continue;
    if (t.department_id !== UNSO_DEPARTMENT_ID) continue;
    const e = plEntryOf(t, today);
    if (e.category === 'excluded' || !monthSet.has(e.month)) continue;
    const account = accountById.get(t.account_id);
    if (!account) {
      unknownAccountCount++;
      continue;
    }
    if (!sectionOf(account)) continue;
    if (e.category === 'forecast') {
      if (e.month === month) {
        gatePending.count++;
        gatePending.amount += e.amount;
      }
      continue;
    }
    const a = accAmounts.get(account.id) ?? emptyMonths(months, 0);
    a[e.month] = (a[e.month] ?? 0) + e.amount;
    accAmounts.set(account.id, a);
    const subs = subAmounts.get(account.id) ?? new Map<string | null, Record<MonthStr, Yen>>();
    const subKey = t.sub_account_id ? t.sub_account_id : null;
    const s = subs.get(subKey) ?? emptyMonths(months, 0);
    s[e.month] = (s[e.month] ?? 0) + e.amount;
    subs.set(subKey, s);
    subAmounts.set(account.id, subs);
  }

  const gateNodes = (section: SectionKey, depth: number): UnsoNode[] =>
    accounts
      .filter((a) => sectionOf(a) === section && (a.active || accAmounts.has(a.id)))
      .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name, 'ja'))
      .map((account) => {
        const n = newNode(`${section}/gate/${account.id}`, account.name, depth, 'leaf', months, 'Gate');
        const subsMap = subAmounts.get(account.id) ?? new Map<string | null, Record<MonthStr, Yen>>();
        const subRows: UnsoNode[] = [];
        const known = subAccounts
          .filter((s) => s.account_id === account.id && (s.active || subsMap.has(s.id)))
          .sort((a, b) => a.display_order - b.display_order);
        for (const s of known) {
          const sn = newNode(`${n.key}/${s.id}`, s.name, depth + 1, 'leaf', months, 'Gate');
          Object.assign(sn.amounts, subsMap.get(s.id) ?? {});
          subRows.push(sn);
        }
        for (const [id, v] of subsMap) {
          if (id === null || known.some((s) => s.id === id)) continue;
          const sn = newNode(`${n.key}/${id}`, subById.get(id)?.name ?? id, depth + 1, 'leaf', months, 'Gate');
          Object.assign(sn.amounts, v);
          subRows.push(sn);
        }
        const unassigned = subsMap.get(null);
        if (unassigned) {
          const sn = newNode(`${n.key}/(none)`, '(科目②未選択)', depth + 1, 'leaf', months, 'Gate');
          Object.assign(sn.amounts, unassigned);
          subRows.push(sn);
        }
        // 開けるのは、金額のある科目②(未選択を除く)があるときだけ。科目②が無い取引だけなら1行のまま
        if (subRows.some((x) => !x.key.endsWith('/(none)') && months.some((m) => (x.amounts[m] ?? 0) !== 0))) {
          n.kind = 'group';
          n.children = subRows;
        } else {
          Object.assign(n.amounts, accAmounts.get(account.id) ?? {});
        }
        return n;
      });

  // --- 区分ごとにまとめる ---
  const section = (key: SectionKey): UnsoNode => {
    const def = SECTION_DEFS.find((d) => d.key === key)!;
    const n = newNode(key, def.label, 0, 'section', months, null);
    if (key === 'sales') n.children = [...salesNodes, ...gateNodes('sales', 1)];
    else if (key === 'cogs') {
      const labor = newNode('cogs/labor', '人件費', 1, 'group', months, 'Routine');
      labor.children = laborNodes;
      const fuel = newNode('cogs/fuel', 'ガソリン代', 1, 'group', months, 'Routine');
      fuel.children = fuelNodes;
      n.children = [...(laborNodes.length ? [labor] : []), ...(fuelNodes.length ? [fuel] : []), ...gateNodes('cogs', 1)];
    } else if (key === 'extra_income') n.children = [...extraTree('extra_income'), ...gateNodes('extra_income', 1)];
    else if (key === 'extra_loss') n.children = [...extraTree('extra_loss'), ...gateNodes('extra_loss', 1)];
    else n.children = gateNodes(key, 1);
    rollUp(n, months);
    return n;
  };

  const s = Object.fromEntries(SECTION_DEFS.map((d) => [d.key, section(d.key)])) as Record<SectionKey, UnsoNode>;
  const profit = (key: string, label: string, f: (m: MonthStr) => Yen): UnsoNode => {
    const n = newNode(`profit/${key}`, label, 0, 'profit', months, null);
    for (const m of months) n.amounts[m] = f(m);
    return n;
  };
  const v = (n: UnsoNode, m: MonthStr): Yen => n.amounts[m] ?? 0;
  const gross = profit('gross', '売上総利益(粗利)', (m) => v(s.sales, m) - v(s.cogs, m));
  const operating = profit('operating', '営業利益', (m) => v(gross, m) - v(s.sga, m));
  const ordinary = profit('ordinary', '経常利益', (m) => v(operating, m) + v(s.non_op_income, m) - v(s.non_op_expense, m));
  const pretax = profit('pretax', '税引前 当期純利益', (m) => v(ordinary, m) + v(s.extra_income, m) - v(s.extra_loss, m));

  return {
    months,
    month,
    rows: [s.sales, s.cogs, gross, s.sga, operating, s.non_op_income, s.non_op_expense, ordinary, s.extra_income, s.extra_loss, pretax],
    routineLineCount: lines.length,
    gatePending,
    unknownAccountCount,
  };
}

/** 選んだ月と、その前の n か月(古い順) */
export function columnMonths(month: MonthStr, previous = 3): MonthStr[] {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return Array.from({ length: previous + 1 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 - (previous - i), 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}` as MonthStr;
  });
}

/** 金額のない行を隠す(Routine の行は金額が0でも出す。Routine から届いた行のため) */
export function hasAmount(n: UnsoNode, months: MonthStr[]): boolean {
  if (n.source === 'Routine' && n.kind === 'leaf') return true;
  if (months.some((m) => (n.amounts[m] ?? 0) !== 0)) return true;
  return n.children.some((c) => hasAmount(c, months));
}

/** 開け閉めできる行の key(子を持つ行) */
export function openableKeys(rows: UnsoNode[]): string[] {
  const out: string[] = [];
  const walk = (n: UnsoNode) => {
    if (n.children.length > 0) out.push(n.key);
    n.children.forEach(walk);
  };
  rows.forEach(walk);
  return out;
}
